import { useState, useEffect, useRef } from 'react';
import { startOfDay, endOfDay } from 'date-fns';
import { useMediaQuery } from 'react-responsive';
import { mediaQueryConstants } from '../../../../constants';

// react-date-range works with { startDate, endDate, key }, while callers of this
// component work with the simpler { start, end } shape. These helpers bridge the two.
const toDraft = (value) => ({
  startDate: value?.start || new Date(),
  endDate: value?.end || new Date(),
  key: 'selection',
});

// react-date-range reports every clicked day at midnight, so an unadjusted draft
// drops the selected end day — and a single-day click asks for a zero-width range.
export const toWholeDays = (draft) => {
  if (!draft?.startDate || !draft?.endDate) return draft;

  return {
    ...draft,
    startDate: startOfDay(draft.startDate),
    endDate: endOfDay(draft.endDate),
  };
};

const fromDraft = (draft) => {
  const { startDate, endDate } = toWholeDays(draft);

  return { start: startDate, end: endDate };
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Mirrors the API's `(range_end - range_start) > MAX_RANGE_DAYS.days` check, which
// compares elapsed time rather than calendar days. Calendar days would disagree
// with it whenever an end date carries a time of day (an end-of-week default is
// 23:59:59.999, so 365 calendar days is 366 days of elapsed time to the server).
export const spanExceedsLimit = (draft, maxSpanDays) => {
  if (!maxSpanDays || !draft?.startDate || !draft?.endDate) return false;

  return draft.endDate - draft.startDate > maxSpanDays * MS_PER_DAY;
};

export default function useDateRangePicker({ value, onChange, maxSpanDays }) {
  // Below this breakpoint the calendar is shown inside a modal instead of a popup.
  const isLargeScreen = useMediaQuery({
    query: `(max-width: ${mediaQueryConstants.LARGE}px)`,
  });

  const [isOpen, setIsOpen] = useState(false);
  // Draft holds the in-progress selection so partial ranges don't commit to the parent.
  const [draft, setDraft] = useState(() => toDraft(value));

  const containerRef = useRef(null);

  // Reset the draft to the committed value every time the picker is opened.
  // Depend on the dates themselves (not the `value` object) so a fresh object
  // literal from the parent on each render doesn't wipe an in-progress selection.
  useEffect(() => {
    if (isOpen) setDraft(toDraft(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, value?.start, value?.end]);

  // Close the floating popup when clicking outside of it (desktop only — the
  // mobile modal handles dismissal on its own).
  useEffect(() => {
    if (!isOpen || isLargeScreen) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isLargeScreen]);

  const toggle = () => setIsOpen((prev) => !prev);
  const close = () => setIsOpen(false);

  const handleSelect = (ranges) => setDraft(ranges.selection);

  // Checked against the whole-day span Apply commits, so the picker refuses
  // exactly the ranges the API would reject.
  const rangeTooWide = spanExceedsLimit(toWholeDays(draft), maxSpanDays);

  const apply = () => {
    if (rangeTooWide) return;

    onChange(fromDraft(draft));
    setIsOpen(false);
  };

  return {
    isOpen,
    isLargeScreen,
    draft,
    containerRef,
    rangeTooWide,
    toggle,
    close,
    handleSelect,
    apply,
  };
}
